import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertCircle, AlertTriangle, Search } from 'lucide-react';
import { Input } from 'components/ui/input';
import EmptyState from 'components/EmptyState';
import { cn } from 'lib/utils';
import type { ImportRowError } from 'api/technicianImportContract';
import { addressGroupLabel, importFieldLabel, rowErrorReasonLabel } from '../importLabels';
import { tableHead, tableRow } from './importTable';

const COLUMNS = 'grid-cols-[95px_160px_1fr_1.4fr]';

const columnIndex = (letters: string): number => letters.toUpperCase().split('').reduce((sum, letter) => sum * 26 + letter.charCodeAt(0) - 64, 0);

/** « column Q », « columns Q–S » when contiguous, else « columns Q, T ». */
const columnsLabel = (t: TFunction, cols: string[]): string => {
  if (cols.length === 0) return '—';
  if (cols.length === 1) return t('column {{col}}', { col: cols[0] });
  const contiguous = cols.every((col, index) => index === 0 || columnIndex(col) === columnIndex(cols[index - 1]) + 1);
  return contiguous
    ? t('columns {{first}}–{{last}}', { first: cols[0], last: cols[cols.length - 1] })
    : t('columns {{cols}}', { cols: cols.join(', ') });
};

// `ignored` comes from the unfiltered errors: a search hiding a row's error must not rename the row.
const rowHeader = (t: TFunction, first: ImportRowError, ignored: boolean): string => {
  if (!ignored) {
    return first.name
      ? t('Row {{row}} · {{name}} — will be imported, to check', { row: first.row, name: first.name })
      : t('Row {{row}} — will be imported, to check', { row: first.row });
  }
  return first.name
    ? t('Row {{row}} · {{name}} — row ignored: none of its changes will be imported', { row: first.row, name: first.name })
    : t('Row {{row}} — row ignored: none of its changes will be imported', { row: first.row });
};

/** CA8 — the rows to correct, one group per row: a single error drops the whole row, a warning never does. */
const ImportErrorsPanel = ({ errors }: { errors: ImportRowError[] }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const ignoredRows = new Set(errors.filter((error) => error.severity === 'error').map((error) => error.row));
  const fieldLabel = (error: ImportRowError): string =>
    (error.severity === 'warning' && addressGroupLabel(t, error.field)) || importFieldLabel(t, error.field);
  const shown = errors.filter(
    (error) => !needle || [String(error.row), error.name ?? '', error.value ?? '', fieldLabel(error)].some((text) => text.toLowerCase().includes(needle))
  );
  const rows = Array.from(
    shown.reduce((byRow, error) => byRow.set(error.row, [...(byRow.get(error.row) ?? []), error]), new Map<number, ImportRowError[]>()).values()
  );

  return (
    <div className="space-y-2">
      {ignoredRows.size > 0 && (
        <p className="text-[12.5px] font-medium text-rose-700 dark:text-rose-400">{t('One error drops its whole row. Correct it, then import the file again.')}</p>
      )}
      <div className="relative w-[260px]">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('Search a technician, a row or a value')}
          aria-label={t('Search a technician, a row or a value')}
          className="pl-8"
        />
      </div>
      {shown.length === 0 ? (
        <EmptyState title={errors.length ? t('No row matches this search') : t('No row to correct')} />
      ) : (
        <div className="overflow-clip rounded-xl border">
          <div className={cn(tableHead, 'sticky top-0', COLUMNS)}>
            <span>{t('Column')}</span><span>{t('Field')}</span><span>{t('Value')}</span><span>{t('Reason')}</span>
          </div>
          {rows.map((group) => (
            <div key={group[0].row}>
              <div className="border-b bg-muted/50 px-3.5 py-2 text-[13px] font-medium">{rowHeader(t, group[0], ignoredRows.has(group[0].row))}</div>
              {group.map((error, index) => {
                const warning = error.severity === 'warning';
                return (
                  <div key={`${error.col}-${index}`} className={cn(tableRow, COLUMNS)}>
                    <span className="text-muted-foreground">{columnsLabel(t, error.cols)}</span>
                    <span>{fieldLabel(error)}</span>
                    <span className="truncate font-mono text-[12.5px]">{error.value || '—'}</span>
                    {warning ? (
                      <span className="flex items-start gap-1.5 text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span><span className="font-semibold">{t('Warning')}</span> · {rowErrorReasonLabel(t, error.reason)}</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-rose-700 dark:text-rose-400">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {rowErrorReasonLabel(t, error.reason)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t('Rows and columns are the Excel ones, to find the cell.')}</p>
    </div>
  );
};

export default ImportErrorsPanel;
